import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, AlertTriangle, FileText, Archive, GraduationCap, UserMinus, History, KeyRound } from 'lucide-react';

type Section =
  | 'evidence'
  | 'incidents'
  | 'disclosures'
  | 'retention'
  | 'offboarding'
  | 'training'
  | 'failed_logins'
  | 'doc_versions';

const SECTIONS: { id: Section; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  { id: 'evidence',     label: 'Evidence Dashboard',   icon: ShieldCheck,   description: 'At-a-glance HIPAA + SOC 2 control posture.' },
  { id: 'incidents',    label: 'Incidents',            icon: AlertTriangle, description: 'Log incidents and track corrective actions.' },
  { id: 'disclosures',  label: 'PHI Disclosures',      icon: FileText,      description: 'HIPAA accounting of disclosures register.' },
  { id: 'retention',    label: 'Retention Schedule',   icon: Archive,       description: 'Record types and required retention periods.' },
  { id: 'training',     label: 'Training Completion',  icon: GraduationCap, description: 'Staff compliance training records.' },
  { id: 'offboarding',  label: 'Staff Offboarding',    icon: UserMinus,     description: 'Access revocation and exit checklist.' },
  { id: 'failed_logins',label: 'Failed Login Activity',icon: KeyRound,      description: 'Recent unsuccessful authentication attempts.' },
  { id: 'doc_versions', label: 'Document Versions',    icon: History,       description: 'Versioned policy and document history.' },
];

export function ComplianceModule() {
  const [active, setActive] = useState<Section>('evidence');

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold">Compliance</h2>
        <p className="text-sm text-muted-foreground">HIPAA + SOC 2 controls, evidence, and workflows in one place.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 border-b pb-3">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const isActive = active === s.id;
          return (
            <Button
              key={s.id}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActive(s.id)}
              className="gap-2 w-full justify-start"
            >
              <Icon className="h-4 w-4" />
              {s.label}
            </Button>
          );
        })}
      </div>

      {active === 'evidence' && <EvidenceDashboard />}
      {active !== 'evidence' && <PlaceholderSection section={SECTIONS.find((s) => s.id === active)!} />}
    </div>
  );
}

function EvidenceDashboard() {
  const tiles = [
    { label: 'Idle timeout',        status: 'Enforced',   detail: '30 min, server-validated' },
    { label: 'Password complexity', status: 'Enforced',   detail: 'NIST rules + HIBP check' },
    { label: 'MFA (admin/manager)', status: 'Required',   detail: 'TOTP enrolled at first login' },
    { label: 'Audit log',           status: 'Append-only',detail: 'Updates/deletes blocked at DB' },
    { label: 'Bed-count signoff',   status: 'Restricted', detail: 'Supervisor or above only' },
    { label: 'Multi-tenant RLS',    status: 'Enforced',   detail: 'Org-scoped on every table'  },
    { label: 'CSP & headers',       status: 'Enforced',   detail: "frame-ancestors 'none'"     },
    { label: 'Retention schedule',  status: 'Documented', detail: 'HIPAA defaults seeded'      },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((t) => (
        <Card key={t.label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="secondary" className="mb-2">{t.status}</Badge>
            <p className="text-xs text-muted-foreground">{t.detail}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PlaceholderSection({ section }: { section: { label: string; description: string; icon: React.ComponentType<{ className?: string }> } }) {
  const Icon = section.icon;
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Icon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-1">{section.label}</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {section.description} Backend tables are live; data entry UI rolls out in the next sprint.
        </p>
      </CardContent>
    </Card>
  );
}

export default ComplianceModule;
